import { QuestionType, type Question } from "@pulse/api/surveyApi";
import { PULSE_FIELD_LIMITS } from "@pulse/features/pulseFieldLimits";
import { z } from "zod";

const commentValidator = z
    .string()
    .max(PULSE_FIELD_LIMITS.answer.comment, "answers.errors.commentMaxLength")
    .optional();

const textAnswerValidator = z
    .string()
    .min(1, "answers.errors.textCannotBeEmpty")
    .max(PULSE_FIELD_LIMITS.answer.text, "answers.errors.textMaxLength");

const ratingAnswerValidator = z
    .number()
    .int("Rating must be an integer")
    .min(1, "Rating must be at least 1")
    .max(5, "Rating must be at most 5");

function multipleChoiceAnswerValidator(question: Question) {
    if (!Array.isArray(question.questionOptions)) {
        throw new Error(`Question ID ${question.id} has invalid options`);
    }

    return z
        .number()
        .int("Choice must be an integer")
        .min(0, "Choice index out of range")
        .max(question.questionOptions.length - 1, "Choice index out of range");
}

function multiSelectAnswerValidator(question: Question) {
    if (!Array.isArray(question.questionOptions)) {
        throw new Error(`Question ID ${question.id} has invalid options`);
    }

    const optionCount = question.questionOptions.length;

    return z
        .array(
            z
                .number()
                .int("Choice must be an integer")
                .min(0, "Choice index out of range")
                .max(optionCount - 1, "Choice index out of range"),
        )
        .min(1, "answers.errors.selectAtLeastOne")
        .max(optionCount, "Too many choices")
        .refine((choices) => new Set(choices).size === choices.length, {
            message: "Choices must be unique",
        });
}

type AnswerValidatorOptions = {
    allowSkippedOptionalAnswer: boolean;
};

export function answerValidator(
    question: Question,
    options: AnswerValidatorOptions = { allowSkippedOptionalAnswer: false },
) {
    let answerSchema: z.ZodTypeAny;

    switch (question.questionType) {
        case QuestionType.TEXT:
            answerSchema = textAnswerValidator;
            break;

        case QuestionType.RATING:
            answerSchema = ratingAnswerValidator;
            break;

        case QuestionType.MULTIPLE_CHOICE:
            answerSchema = multipleChoiceAnswerValidator(question);
            break;

        case QuestionType.MULTI_SELECT:
            answerSchema = multiSelectAnswerValidator(question);
            break;

        default:
            throw new Error("Unsupported question type");
    }

    const answerValueSchema =
        question.optional && options.allowSkippedOptionalAnswer
            ? answerSchema.optional()
            : answerSchema;

    return z.object({
        answerValue: answerValueSchema,
        answerComment: commentValidator,
        surveyQuestionId: z.literal(question.id),
    });
}

export function questionValidator(questions: Question[]) {
    const questionValidators = questions.map((question) =>
        answerValidator(question, { allowSkippedOptionalAnswer: true }),
    ) as unknown as [z.ZodTypeAny, ...z.ZodTypeAny[]];

    return z.object({
        answers: z.tuple(questionValidators),
    });
}
